import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes } from '../lib/api';

function RecipeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prepTime: '',
    cookTime: '',
    totalTime: '',
    servings: '',
    ingredients: [],
    instructions: [],
    notes: [],
    userNotes: '',
  });

  const { data: recipe, isLoading } = useQuery({
    queryKey: ['recipe', id],
    queryFn: () => recipes.get(id),
  });

  useEffect(() => {
    if (recipe) {
      setFormData({
        title: recipe.title || '',
        description: recipe.description || '',
        prepTime: recipe.prepTime || '',
        cookTime: recipe.cookTime || '',
        totalTime: recipe.totalTime || '',
        servings: recipe.servings || '',
        ingredients: recipe.ingredients || [],
        instructions: recipe.instructions || [],
        notes: recipe.notes || [],
        userNotes: recipe.userNotes || '',
      });
    }
  }, [recipe]);

  const updateMutation = useMutation({
    mutationFn: (data) => recipes.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      navigate(`/recipe/${id}`);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { item: '', amount: '', unit: '', notes: '' }],
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, { step: formData.instructions.length + 1, text: '' }],
    });
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = { step: index + 1, text: value };
    setFormData({ ...formData, instructions: newInstructions });
  };

  const removeInstruction = (index) => {
    const newInstructions = formData.instructions
      .filter((_, i) => i !== index)
      .map((inst, i) => ({ ...inst, step: i + 1 }));
    setFormData({ ...formData, instructions: newInstructions });
  };

  const addNote = () => {
    setFormData({
      ...formData,
      notes: [...formData.notes, ''],
    });
  };

  const updateNote = (index, value) => {
    const newNotes = [...formData.notes];
    newNotes[index] = value;
    setFormData({ ...formData, notes: newNotes });
  };

  const removeNote = (index) => {
    setFormData({
      ...formData,
      notes: formData.notes.filter((_, i) => i !== index),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Recipe</h1>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input min-h-[100px]"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Prep Time
              </label>
              <input
                type="text"
                value={formData.prepTime}
                onChange={(e) => setFormData({ ...formData, prepTime: e.target.value })}
                className="input"
                placeholder="15 mins"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cook Time
              </label>
              <input
                type="text"
                value={formData.cookTime}
                onChange={(e) => setFormData({ ...formData, cookTime: e.target.value })}
                className="input"
                placeholder="30 mins"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Total Time
              </label>
              <input
                type="text"
                value={formData.totalTime}
                onChange={(e) => setFormData({ ...formData, totalTime: e.target.value })}
                className="input"
                placeholder="45 mins"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Servings
              </label>
              <input
                type="text"
                value={formData.servings}
                onChange={(e) => setFormData({ ...formData, servings: e.target.value })}
                className="input"
                placeholder="4 servings"
              />
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ingredients</h2>
            <button type="button" onClick={addIngredient} className="btn btn-secondary text-sm">
              + Add Ingredient
            </button>
          </div>

          <div className="space-y-3">
            {formData.ingredients.map((ing, index) => (
              <div key={index} className="flex gap-2 items-start">
                <input
                  type="text"
                  value={ing.amount}
                  onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                  className="input w-20"
                  placeholder="Qty"
                />
                <input
                  type="text"
                  value={ing.unit}
                  onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                  className="input w-24"
                  placeholder="Unit"
                />
                <input
                  type="text"
                  value={ing.item}
                  onChange={(e) => updateIngredient(index, 'item', e.target.value)}
                  className="input flex-1"
                  placeholder="Ingredient"
                />
                <input
                  type="text"
                  value={ing.notes}
                  onChange={(e) => updateIngredient(index, 'notes', e.target.value)}
                  className="input w-32"
                  placeholder="Notes"
                />
                <button
                  type="button"
                  onClick={() => removeIngredient(index)}
                  className="btn-icon text-red-500"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}

            {formData.ingredients.length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No ingredients yet. Click "Add Ingredient" to start.
              </p>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Instructions</h2>
            <button type="button" onClick={addInstruction} className="btn btn-secondary text-sm">
              + Add Step
            </button>
          </div>

          <div className="space-y-3">
            {formData.instructions.map((inst, index) => (
              <div key={index} className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </span>
                <textarea
                  value={inst.text}
                  onChange={(e) => updateInstruction(index, e.target.value)}
                  className="input flex-1 min-h-[80px]"
                  placeholder="Describe this step..."
                  rows={2}
                />
                <button
                  type="button"
                  onClick={() => removeInstruction(index)}
                  className="btn-icon text-red-500"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}

            {formData.instructions.length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No instructions yet. Click "Add Step" to start.
              </p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notes</h2>
            <button type="button" onClick={addNote} className="btn btn-secondary text-sm">
              + Add Note
            </button>
          </div>

          <div className="space-y-3">
            {formData.notes.map((note, index) => (
              <div key={index} className="flex gap-2 items-start">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => updateNote(index, e.target.value)}
                  className="input flex-1"
                  placeholder="Add a tip or variation..."
                />
                <button
                  type="button"
                  onClick={() => removeNote(index)}
                  className="btn-icon text-red-500"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Personal Notes
            </label>
            <textarea
              value={formData.userNotes}
              onChange={(e) => setFormData({ ...formData, userNotes: e.target.value })}
              className="input min-h-[100px]"
              rows={3}
              placeholder="Add your own notes about this recipe..."
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn btn-primary"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {updateMutation.error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
            {updateMutation.error.message}
          </div>
        )}
      </form>
    </div>
  );
}

export default RecipeEdit;
